import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { ptBR } from "date-fns/locale";
import React, { useEffect, useState } from "react";

import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { TaskInterface } from "../../common/interfaces";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import {
  TaskScopeHint,
  TaskScopeLabel,
  TypeTaskScope
} from "../enum/taskScopeEnum";
import {
  TASK_COLUMNS,
  TaskStatusLabel,
  TypeTaskStatus
} from "../enum/taskStatusEnum";
import PageToaster from "../toaster/PageToaster";

/** Só o que o seletor de responsáveis precisa saber de um usuário. */
interface AssignableUser {
  user_id: number;
  user_name: string;
}

interface TaskCreateModalProps {
  open: boolean;
  onClose: () => void;
  /** Quando enviado, o modal abre em modo edição. */
  task?: TaskInterface | null;
}

const TaskCreateModal: React.FC<TaskCreateModalProps> = ({
  open,
  onClose,
  task
}) => {
  const [taskTitle, setTaskTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [statusValue, setStatusValue] = useState<TypeTaskStatus>(
    TypeTaskStatus.TODO
  );

  const [scope, setScope] = useState<TypeTaskScope>(TypeTaskScope.INDIVIDUAL);
  const [assignees, setAssignees] = useState<AssignableUser[]>([]);
  const [userOptions, setUserOptions] = useState<AssignableUser[]>([]);
  const [userSearch, setUserSearch] = useState("");

  const [isUpdate, setIsUpdate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const containerId = "toaster-task-create-modal";
  const { autoSuccess, autoError } = usePageToast(containerId, true);

  const handleHttpError = makeToasterErrorHandler(autoError);

  // Pré-preenche em edição, limpa em criação.
  useEffect(() => {
    if (task) {
      setIsUpdate(true);
      setTaskTitle(task.task_title);
      setDescription(task.task_description ?? "");
      setDueDate(new Date(task.task_due_date));
      setStatusValue(task.task_status);
      setScope(task.task_scope);
      setAssignees(
        (task.assignees ?? []).map((a) => ({
          user_id: a.TB_USER.user_id,
          user_name: a.TB_USER.user_name
        }))
      );
    } else {
      setIsUpdate(false);
      setTaskTitle("");
      setDescription("");
      setDueDate(null);
      setStatusValue(TypeTaskStatus.TODO);
      setScope(TypeTaskScope.INDIVIDUAL);
      setAssignees([]);
    }
  }, [task, open]);

  // Lista de quem pode ser responsável. Só busca no modo grupo — no individual
  // o campo nem aparece.
  useEffect(() => {
    if (!open || scope !== TypeTaskScope.GROUP) return;

    let active = true;

    api
      .get("/user", {
        params: { page: 1, limit: 20, user_name: userSearch || undefined }
      })
      .then((res) => {
        if (!active) return;
        setUserOptions(
          (res.data.items || []).map((u: any) => ({
            user_id: u.user_id,
            user_name: u.user_name
          }))
        );
      })
      .catch(() => {
        if (active) setUserOptions([]);
      });

    return () => {
      active = false;
    };
  }, [open, scope, userSearch]);

  const handleSubmit = async () => {
    if (taskTitle.trim().length < 3) {
      autoError("Nome da tarefa deve ter no mínimo 3 caracteres.");
      return;
    }

    if (dueDate && isNaN(dueDate.getTime())) {
      autoError("Data limite inválida.");
      return;
    }

    // Data no passado já nasceria vencida e sairia do quadro na próxima cron.
    if (!isUpdate && dueDate && dueDate.getTime() <= Date.now()) {
      autoError("A data limite deve ser maior que a data atual.");
      return;
    }

    setIsSubmitting(true);

    // Sem data, o campo nem vai no payload: quem decide o prazo padrão
    // (uma semana à frente ao meio-dia, fugindo do fim de semana) é o backend,
    // para a regra valer venha a chamada de onde vier.
    const payload = {
      task_title: taskTitle.trim(),
      task_description: description.trim() || undefined,
      task_due_date: dueDate ? dueDate.toISOString() : undefined,
      task_status: statusValue,
      task_scope: scope,
      // Lista vazia é intencional em tarefa de grupo: significa "livre, para
      // quem pegar". Por isso vai sempre, e não só quando tem gente.
      assignee_ids:
        scope === TypeTaskScope.GROUP
          ? assignees.map((u) => u.user_id)
          : []
    };

    try {
      if (isUpdate && task) {
        await api.put(`/task/${task.task_id}`, payload);
        autoSuccess("Tarefa atualizada com sucesso!");
      } else {
        await api.post("/task", payload);
        autoSuccess("Tarefa criada com sucesso!");
      }

      onClose();
    } catch (err: any) {
      console.error("Erro ao salvar tarefa:", err.response?.data || err);
      handleHttpError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal open={open} onClose={onClose} aria-labelledby="modal-task">
        <Box
          sx={{
            position: "absolute" as const,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
            width: { xs: "90vw", sm: "500px" },
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              {isUpdate ? "Editar Tarefa" : "Nova Tarefa"}
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <FormControl fullWidth margin="normal">
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={scope}
              onChange={(_, value) => {
                if (value) setScope(value as TypeTaskScope);
              }}
            >
              {(Object.values(TypeTaskScope) as TypeTaskScope[]).map(
                (option) => (
                  <ToggleButton key={option} value={option}>
                    {TaskScopeLabel[option]}
                  </ToggleButton>
                )
              )}
            </ToggleButtonGroup>
            <FormHelperText>{TaskScopeHint[scope]}</FormHelperText>
          </FormControl>

          <TextField
            fullWidth
            label="Nome da Tarefa"
            value={taskTitle}
            onChange={(e) => setTaskTitle(e.target.value)}
            margin="normal"
            inputProps={{ maxLength: 120 }}
          />

          <TextField
            fullWidth
            label="Descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            multiline
            minRows={3}
            inputProps={{ maxLength: 255 }}
          />

          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={ptBR}
          >
            <DateTimePicker
              label="Data Limite"
              value={dueDate}
              onChange={setDueDate}
              format="dd/MM/yyyy HH:mm"
              disablePast={!isUpdate}
              viewRenderers={{
                hours: renderTimeViewClock,
                minutes: renderTimeViewClock,
                seconds: renderTimeViewClock
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "normal",
                  helperText: isUpdate
                    ? undefined
                    : "Em branco: uma semana à frente, ao meio-dia (segunda, se cair no fim de semana)."
                }
              }}
            />
          </LocalizationProvider>

          {scope === TypeTaskScope.GROUP && (
            <Autocomplete
              multiple
              options={userOptions}
              value={assignees}
              onChange={(_, value) => setAssignees(value)}
              onInputChange={(_, value) => setUserSearch(value)}
              getOptionLabel={(option) => option.user_name}
              isOptionEqualToValue={(option, value) =>
                option.user_id === value.user_id
              }
              filterSelectedOptions
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    size="small"
                    label={option.user_name}
                    {...getTagProps({ index })}
                    key={option.user_id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Responsáveis (pode escolher mais de um)"
                  margin="normal"
                  placeholder={
                    assignees.length ? "Adicionar mais alguém" : "Buscar pessoa"
                  }
                  helperText={
                    assignees.length
                      ? `${assignees.length} pessoa(s) nesta tarefa. Continue digitando para somar mais.`
                      : "Vazio = tarefa livre, para quem pegar para fazer."
                  }
                />
              )}
            />
          )}

          <FormControl fullWidth margin="normal">
            <InputLabel id="label-task-status">Situação</InputLabel>
            <Select
              labelId="label-task-status"
              value={statusValue}
              label="Situação"
              onChange={(e) =>
                setStatusValue(e.target.value as TypeTaskStatus)
              }
            >
              {TASK_COLUMNS.map((statusCode) => (
                <MenuItem key={statusCode} value={statusCode}>
                  {TaskStatusLabel[statusCode]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button onClick={onClose} color="secondary" sx={{ mr: 2 }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isUpdate ? "Atualizar" : "Cadastrar"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default TaskCreateModal;
